import React, { useEffect } from "react";

import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { format } from "date-fns";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useTransition } from "@react-spring/web";

import Text from "src/components/text";

import { useReorderCustomFields } from "src/query/settings/customFields";
import { CustomField } from "src/types/settings/customFields";
import Tooltip from "src/components/tooltip";

import _ from "lodash";
import styles from "./styles.module.scss";
import cx from "classnames";

const Salesforce = '/images/settings/custom_fields/salesforce.svg';
const MSDynamics = '/images/settings/custom_fields/msDynamics.png';

interface MenuPaneProps {
  items: CustomField[];
  selectedCustomField: CustomField | null;
  onSelectCustomField: (item: CustomField) => void;
  isCreatingNewField?: boolean;
}

// This component renders a sortable list of custom fields in the settings menu pane.
const SortableFieldItem: React.FC<{
  item: CustomField;
  isSelected: boolean;
  onSelectItem: (item: CustomField) => void;
}> = ({ item, isSelected, onSelectItem }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });


// This function returns the icon based on the integration type of the custom field.
  const getIntegrationIcon = (type?: string) => {
    switch (type) {
      case "salesforce_v2":
        return Salesforce;
      case "ms_dynamics_v2":
        return MSDynamics;
      default:
        return null;
    }
  }

// This function returns the label for the integration based on its type.

  const getIntegrationLabel = (type?: string) => {
    switch (type) {
      case "salesforce_v2":
        return "Salesforce";
      case "ms_dynamics_v2":
        return "MS Dynamics";
      default:
        return "";
    }
  }

// This function returns the tooltip content based on the integration type.
  const getTooltipContent = (type?: string) => {
    switch (type) {
      case "salesforce_v2":
        return "Salesforce Opportunity field";
      case "ms_dynamics_v2":
        return "MS Dynamics Opportunity field";
      default:
        return "";
    }
  };

  const icon = getIntegrationIcon(item.integrationType);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    cursor: isDragging ? "move" : "pointer",
  };

  const formatDate = (iso: string | number) => {
    return format(new Date(iso), "do MMM yyyy");
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cx(styles.fieldItem, isSelected && styles.selected)}
      onClick={() => onSelectItem(item)}
    >
      <div className={styles.listIcon}>
        <i className="paperflite-drag" />
      </div>
      <div className={styles.item}>
        <div className={styles.info}>
          <div className={cx(styles.title, isSelected && styles.selected)}>
            <Text text={item.name} lines={1} tooltip={{ placement: "auto" }} className={styles.name}/>
          </div>
          <div className={styles.meta}>
            <span>{item.options.length} list value</span>
            {!item.integrationType && (
              <span>last updated: {formatDate(item.modifiedDate)}</span>
            )}
          </div>
        </div>
        {icon && (
          <Tooltip content={getTooltipContent(item.integrationType)} placement="top">
            <div className={styles.integrationLogo}>
            <img src={icon} alt="" />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const MenuPane: React.FC<MenuPaneProps> = ({ items, selectedCustomField, onSelectCustomField, isCreatingNewField }) => {

  const [updatedItems, setUpdatedItems] = React.useState(items);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const reorderMutation = useReorderCustomFields();

  // This function handles the drag end event, reordering the items based on the drag and drop interaction.
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setUpdatedItems(newItems);

      const reorderedIds = newItems.map((item) => item.id);
      reorderMutation.mutate(reorderedIds, {
        onError: () => {
          setUpdatedItems(updatedItems);
        },
      });
    }
  };

  useEffect(() => {
    setUpdatedItems(items);
  }, [items]);

  return (
    <div
    className={styles.menuPane}
  >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[
          restrictToFirstScrollableAncestor,
          restrictToVerticalAxis             
        ]}
      >
        <div className={styles.scrollBody}>
          <SortableContext items={updatedItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            {updatedItems.map((item) => (
              <div
                key={item.id}
                className={cx(
                  styles.itemWrapper,
                  isCreatingNewField && styles.itemEnter,
                  !isCreatingNewField && styles.itemLeave
                )}
              >
                {selectedCustomField?.id === item.id && <div className={styles.selectedFieldLine} />}
                <SortableFieldItem
                  item={item}
                  isSelected={selectedCustomField?.id === item.id}
                  onSelectItem={onSelectCustomField}
                />
              </div>
            ))}
          </SortableContext>
        </div>
      </DndContext>
    </div>
  );
};

export default MenuPane;